import { useState, useEffect } from 'react';
import { Search, Bell, Activity, AlertTriangle, Shield } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AgentInteraction, SalesIntentSignal, GuardrailRule, AgentConfig } from '../../types/agent';
import { MetricCard, Badge, EmptyState } from '../shared';

export function AgentDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);
  const [interactions, setInteractions] = useState<AgentInteraction[]>([]);
  const [recentInteractions, setRecentInteractions] = useState<AgentInteraction[]>([]);
  const [intentSignals, setIntentSignals] = useState<SalesIntentSignal[]>([]);
  const [activeGuardrails, setActiveGuardrails] = useState<GuardrailRule[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      // Load agent config
      const configQuery = query(
        collection(db, 'agentConfigs'),
        where('userId', '==', user.uid),
        limit(1)
      );
      const configSnap = await getDocs(configQuery);
      if (!configSnap.empty) {
        setAgentConfig(configSnap.docs[0].data() as AgentConfig);
      }

      // Load interactions
      const interactionsQuery = query(
        collection(db, 'agentInteractions'),
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const interactionsSnap = await getDocs(interactionsQuery);
      const interactionsData = interactionsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as AgentInteraction);
      setInteractions(interactionsData);
      setRecentInteractions(interactionsData.slice(0, 10));

      // Load sales intent signals
      const intentQuery = query(
        collection(db, 'salesIntentSignals'),
        where('userId', '==', user.uid),
        where('intentLevel', 'in', ['high', 'medium']),
        orderBy('timestamp', 'desc'),
        limit(5)
      );
      const intentSnap = await getDocs(intentQuery);
      setIntentSignals(intentSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as SalesIntentSignal));

      // Load active guardrails
      const guardrailsQuery = query(
        collection(db, 'guardrailRules'),
        where('userId', '==', user.uid),
        where('enabled', '==', true)
      );
      const guardrailsSnap = await getDocs(guardrailsQuery);
      setActiveGuardrails(guardrailsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }) as GuardrailRule));

    } catch (error) {
      console.error('Error loading agent dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalInteractions = interactions.length;
  const escalations = interactions.filter(i => i.escalated).length;
  const highIntentCount = intentSignals.filter(s => s.intentLevel === 'high').length;

  // Calculate avg response time (mock for now)
  const avgResponseTime = 1.2; // seconds

  const agentStatus: 'live' | 'paused' | 'error' =
    !agentConfig ? 'paused' :
    agentConfig.deploymentStatus.mcp === 'active' ? 'live' :
    agentConfig.deploymentStatus.mcp === 'error' ? 'error' :
    'paused';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Dashboard</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Agent Status Pill */}
          <div
            className={`flex items-center gap-2 px-3 py-1 rounded-full ${
              agentStatus === 'live'
                ? 'bg-success-green text-white'
                : agentStatus === 'error'
                ? 'bg-critical-red text-white'
                : 'bg-gray-600 text-white'
            }`}
          >
            <Activity className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase">
              {agentStatus === 'live' ? 'Agent Live' : agentStatus === 'error' ? 'Error' : 'Paused'}
            </span>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search interactions..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
          <h1 className="text-3xl font-semibold text-gray-900">Agent Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">Monitor your brand agent performance and interactions</p>
        </div>

        {/* 4-Stat Row */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <MetricCard
            label="Total Interactions"
            value={totalInteractions.toString()}
            
          />
          <MetricCard
            label="Avg Response Time"
            value={`${avgResponseTime}s`}
            
          />
          <MetricCard
            label="Intent Signals"
            value={intentSignals.length.toString()}
            
          />
          <MetricCard
            label="Escalations"
            value={escalations.toString()}
            
          />
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Live Interaction Feed */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Live Interaction Feed</h2>
              <Badge variant={agentStatus === 'live' ? 'success' : 'critical'}>
                {agentStatus}
              </Badge>
            </div>

            {recentInteractions.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="No interactions yet"
                description="Interactions will appear here as users engage with your agent"
              />
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recentInteractions.map(interaction => (
                  <div
                    key={interaction.id}
                    className={`p-3 rounded-lg border ${
                      interaction.escalated
                        ? 'border-critical-red bg-red-50'
                        : interaction.intentLevel === 'high'
                        ? 'border-t3-primary bg-purple-50'
                        : 'border-gray-200 bg-surface-warm'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="primary">{interaction.aiSystem}</Badge>
                        {interaction.intentLevel !== 'none' && (
                          <Badge
                            variant={
                              interaction.intentLevel === 'high'
                                ? 'critical'
                                : interaction.intentLevel === 'medium'
                                ? 'warning'
                                : 'primary'
                            }
                          >
                            {interaction.intentLevel} intent
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-600">
                        {interaction.timestamp.toDate().toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-2 mb-1">{interaction.query}</p>
                    {interaction.escalated && (
                      <div className="flex items-center gap-1 text-xs text-critical-red">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Escalated</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sales Intent Signals */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">High Intent Signals</h2>
              <span className="text-sm text-gray-600">{highIntentCount} high</span>
            </div>

            {intentSignals.length === 0 ? (
              <EmptyState
                icon={AlertTriangle}
                title="No intent signals"
                description="High intent interactions will be highlighted here"
              />
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {intentSignals.map(signal => (
                  <div
                    key={signal.id}
                    className={`p-3 rounded-lg border ${
                      signal.intentLevel === 'high'
                        ? 'border-t3-primary bg-purple-50'
                        : 'border-warning-amber bg-yellow-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge
                        variant={signal.intentLevel === 'high' ? 'critical' : 'warning'}
                      >
                        {signal.intentLevel} intent
                      </Badge>
                      <span className="text-xs text-gray-600">
                        {signal.timestamp.toDate().toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-2 mb-2">{signal.query}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {signal.signals.slice(0, 3).map((s, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-white px-2 py-1 rounded border border-gray-200"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                    {!signal.routed && (
                      <button className="mt-2 text-sm text-t3-primary hover:underline font-medium">
                        Route to Sales →
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active Guardrails */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-t3-primary" />
            <h2 className="text-base font-semibold text-gray-900">Active Guardrails</h2>
            <span className="text-sm text-gray-600">({activeGuardrails.length})</span>
          </div>

          {activeGuardrails.length === 0 ? (
            <EmptyState
              icon={Shield}
              title="No active guardrails"
              description="Configure guardrails to protect your brand voice and enforce policies"
            />
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {activeGuardrails.slice(0, 6).map(rule => (
                <div key={rule.id} className="p-4 bg-surface-warm rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">{rule.name}</h3>
                    <Badge variant="success">Active</Badge>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 mb-2">{rule.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="primary">{rule.action}</Badge>
                    <span className="text-xs text-gray-500">P{rule.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeGuardrails.length > 6 && (
            <div className="mt-4 text-center">
              <button className="text-sm text-t3-primary hover:underline">
                View all {activeGuardrails.length} guardrails →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
